import React from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { memberTabAdminActionDone } from '@wix/bi-logger-groups/v2';

import type { IGroupMember } from '@wix/social-groups-serverless/dist/members/types';
import { selectRemoveStatus } from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { Button } from 'wui/Button';
import { AlertDialog } from 'wui/AlertDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogActions } from 'wui/DialogActions';
import { DialogContent } from 'wui/DialogContent';
import { Typography } from 'wui/Typography';

export interface IRemoveMemberDialogProps {
  isOpen: boolean;
  groupId: string;
  member: IGroupMember;

  onClose(): void;
}

export function RemoveMemberDialog(props: IRemoveMemberDialogProps) {
  const { isOpen, member, groupId, onClose } = props;

  const bi = useBi();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const { members$ } = useController();
  const status = useSelector(selectRemoveStatus(member.siteMemberId as string));

  useDidUpdate(() => {
    if (!status.loading && !status.error) {
      onClose();
    }
  }, [status.loading, status.error]);

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="rm-dialog-title"
      aria-describedby="rm-dialog-content"
    >
      <DialogTitle
        alert
        id="rm-dialog-title"
        title={t('groups-web.group.actions.remove-member.modal.title')}
      />
      <DialogContent alert>
        <Typography align="center" id="rm-dialog-content">
          {t('groups-web.group.actions.remove-member.modal.caption', {
            memberFullName: member.name,
          })}
        </Typography>
      </DialogContent>
      <DialogActions alert>
        <Button outlined variant="basic" fullWidth={isMobile} onClick={onClose}>
          {t('groups-web.cancel')}
        </Button>
        <Button
          variant="basic"
          fullWidth={isMobile}
          onClick={handleSubmit}
          disabled={status.loading}
          loading={status.loading}
          data-hook="ok button"
        >
          {t('groups-web.group.actions.remove-member.modal.confirm')}
        </Button>
      </DialogActions>
    </AlertDialog>
  );

  function handleSubmit() {
    members$.remove(groupId, member.siteMemberId as string);
    bi.report(
      memberTabAdminActionDone({
        action: 'remove_member',
        group_id: groupId,
        origin: 'dialog_screen_btn_clk',
        site_member_id: member.siteMemberId as string,
      }),
    );
  }
}

RemoveMemberDialog.displayName = 'RemoveMemberDialog';
